import React, { useEffect, useState, useRef } from 'react';
import { motion, PanInfo } from 'framer-motion';
import { XIcon, MinusIcon, MaximizeIcon, ChevronLeftIcon, Minimize2Icon } from 'lucide-react';
import { useWindows, WindowState } from '../../contexts/WindowContext';
import { useTheme } from '../../contexts/ThemeContext';
import { useNavigate } from 'react-router-dom';
interface WindowProps {
  window: WindowState;
  children: React.ReactNode;
}
export function Window({
  window,
  children
}: WindowProps) {
  const {
    closeWindow,
    minimizeWindow,
    maximizeWindow,
    focusWindow,
    updateWindowPosition,
    updateWindowSize,
    isMobile,
    windows
  } = useWindows();
  const {
    t,
    getThemeClasses,
    reduceMotion,
    theme,
    highContrast
  } = useTheme();
  const navigate = useNavigate();
  const [isDragging, setIsDragging] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const [resizeDirection, setResizeDirection] = useState<string | null>(null);
  const windowRef = useRef<HTMLDivElement>(null);
  const themeClasses = getThemeClasses();
  // Focus management
  useEffect(() => {
    if (!isMobile && windowRef.current) {
      const isTopWindow = windows.every(w => w.zIndex <= window.zIndex);
      if (isTopWindow) {
        const focusableElements = windowRef.current.querySelectorAll('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])');
        if (focusableElements.length > 0) {
          const firstElement = focusableElements[0] as HTMLElement;
          firstElement.focus();
        }
      }
    }
  }, [window.zIndex, windows, isMobile]);
  if (window.isMinimized) return null;
  const handleDragEnd = (event: MouseEvent | TouchEvent | PointerEvent, info: PanInfo) => {
    setIsDragging(false);
    updateWindowPosition(window.id, {
      x: window.position.x + info.offset.x,
      y: window.position.y + info.offset.y
    });
  };
  const handleClose = (e: React.MouseEvent) => {
    e.stopPropagation();
    // Find remaining active windows before closing
    const remainingActiveWindows = windows.filter(w => w.id !== window.id && !w.isMinimized);
    // Close the window immediately
    closeWindow(window.id);
    // Handle navigation after closing
    if (remainingActiveWindows.length === 0) {
      // No more windows, go to home
      navigate('/', {
        replace: true
      });
    } else {
      // Navigate to the top remaining window
      const topWindow = remainingActiveWindows.reduce((prev, current) => current.zIndex > prev.zIndex ? current : prev, remainingActiveWindows[0]);
      navigate(`/${topWindow.appId}`, {
        replace: true
      });
    }
  };
  const handleMinimize = (e: React.MouseEvent) => {
    e.stopPropagation();
    // Check if this window is currently active (not minimized)
    const isCurrentlyActive = !window.isMinimized;
    // Minimize/restore the window
    minimizeWindow(window.id);
    // Only handle navigation if we're minimizing (not restoring)
    if (isCurrentlyActive) {
      const remainingActiveWindows = windows.filter(w => w.id !== window.id && !w.isMinimized);
      if (remainingActiveWindows.length === 0) {
        navigate('/', {
          replace: true
        });
      } else {
        const topWindow = remainingActiveWindows.reduce((prev, current) => current.zIndex > prev.zIndex ? current : prev, remainingActiveWindows[0]);
        navigate(`/${topWindow.appId}`, {
          replace: true
        });
      }
    }
  };
  const handleMaximize = (e: React.MouseEvent) => {
    e.stopPropagation();
    maximizeWindow(window.id);
  };
  // Resize handlers
  const handleResizeStart = (direction: string) => (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsResizing(true);
    setResizeDirection(direction);
    focusWindow(window.id);
  };
  useEffect(() => {
    if (!isResizing || !resizeDirection || isMobile) return;
    const handleMouseMove = (e: MouseEvent) => {
      const deltaX = e.movementX;
      const deltaY = e.movementY;
      let newWidth = window.size.width;
      let newHeight = window.size.height;
      let newX = window.position.x;
      let newY = window.position.y;
      // Handle different resize directions
      if (resizeDirection.includes('e')) {
        newWidth = Math.max(400, window.size.width + deltaX);
      }
      if (resizeDirection.includes('w')) {
        const widthChange = Math.min(deltaX, window.size.width - 400);
        newWidth = window.size.width - widthChange;
        newX = window.position.x + widthChange;
      }
      if (resizeDirection.includes('s')) {
        newHeight = Math.max(300, window.size.height + deltaY);
      }
      if (resizeDirection.includes('n')) {
        const heightChange = Math.min(deltaY, window.size.height - 300);
        newHeight = window.size.height - heightChange;
        newY = window.position.y + heightChange;
      }
      updateWindowSize(window.id, {
        width: newWidth,
        height: newHeight
      });
      updateWindowPosition(window.id, {
        x: newX,
        y: newY
      });
    };
    const handleMouseUp = () => {
      setIsResizing(false);
      setResizeDirection(null);
    };
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isResizing, resizeDirection, window, updateWindowSize, updateWindowPosition, isMobile]);
  // Mobile view
  if (isMobile) {
    return <motion.div ref={windowRef} initial={{
      x: '100%'
    }} animate={{
      x: 0
    }} exit={{
      x: '100%'
    }} transition={{
      type: reduceMotion ? 'tween' : 'spring',
      damping: 30,
      stiffness: 250
    }} style={{
      position: 'fixed',
      inset: 0,
      zIndex: window.zIndex
    }} className={themeClasses.bg} role="dialog" aria-modal="true" aria-labelledby={`window-title-${window.id}`} data-id="element-879">
        <div className={`h-14 ${themeClasses.card} border-b ${themeClasses.border} flex items-center justify-between px-4 backdrop-blur-xl`} data-id="element-880">
          <button onClick={handleClose} className={`flex items-center space-x-2 ${themeClasses.text} transition-opacity hover:opacity-70`} aria-label={`Close ${window.title}`} data-id="element-881">
            <ChevronLeftIcon className="w-5 h-5" strokeWidth={2} data-id="element-882" />
            <span className="text-sm font-medium" data-id="element-883">{t.common.back}</span>
          </button>
          <span id={`window-title-${window.id}`} className={`${themeClasses.text} text-sm font-semibold`} data-id="element-884">
            {window.title}
          </span>
          <button onClick={handleMinimize} className={`p-2 ${themeClasses.cardHover} rounded-lg transition-colors`} aria-label={`Minimize ${window.title}`} data-id="element-885">
            <MinusIcon className={`w-5 h-5 ${themeClasses.icon}`} strokeWidth={2} data-id="element-886" />
          </button>
        </div>
        <div className="h-[calc(100%-3.5rem)] overflow-auto pb-20 custom-scrollbar" data-id="element-887">
          {children}
        </div>
      </motion.div>;
  }
  // Desktop view with resize handles
  return <motion.div ref={windowRef} drag={!isResizing} dragMomentum={false} dragElastic={0} onDragStart={() => {
    setIsDragging(true);
    focusWindow(window.id);
  }} onDragEnd={handleDragEnd} initial={reduceMotion ? {} : {
    scale: 0.95,
    opacity: 0
  }} animate={{
    ...(reduceMotion ? {} : {
      scale: 1,
      opacity: 1
    }),
    x: window.isMaximized ? 0 : window.position.x,
    y: window.isMaximized ? 0 : window.position.y,
    width: window.isMaximized ? '100vw' : window.size.width,
    height: window.isMaximized ? '100vh' : window.size.height
  }} exit={reduceMotion ? {} : {
    scale: 0.95,
    opacity: 0
  }} style={{
    position: 'absolute',
    zIndex: window.zIndex
  }} className={`rounded-2xl overflow-hidden ${themeClasses.card} border ${themeClasses.border} backdrop-blur-2xl shadow-2xl ${theme === 'neon' ? 'shadow-teal-500/10' : theme === 'light' ? 'shadow-indigo-500/10' : 'shadow-violet-500/10'}`} onMouseDown={() => focusWindow(window.id)} role="dialog" aria-modal="true" aria-labelledby={`window-title-${window.id}`} data-id="element-888">
      {/* Resize handles - only show when not maximized */}
      {!window.isMaximized && <>
          {/* Corner handles */}
          <div onMouseDown={handleResizeStart('nw')} className="absolute top-0 left-0 w-4 h-4 cursor-nw-resize z-50" style={{
        touchAction: 'none'
      }} data-id="element-889" />
          <div onMouseDown={handleResizeStart('ne')} className="absolute top-0 right-0 w-4 h-4 cursor-ne-resize z-50" style={{
        touchAction: 'none'
      }} data-id="element-890" />
          <div onMouseDown={handleResizeStart('sw')} className="absolute bottom-0 left-0 w-4 h-4 cursor-sw-resize z-50" style={{
        touchAction: 'none'
      }} data-id="element-891" />
          <div onMouseDown={handleResizeStart('se')} className="absolute bottom-0 right-0 w-4 h-4 cursor-se-resize z-50" style={{
        touchAction: 'none'
      }} data-id="element-892" />
          {/* Edge handles */}
          <div onMouseDown={handleResizeStart('n')} className="absolute top-0 left-4 right-4 h-2 cursor-n-resize z-40" style={{
        touchAction: 'none'
      }} data-id="element-893" />
          <div onMouseDown={handleResizeStart('s')} className="absolute bottom-0 left-4 right-4 h-2 cursor-s-resize z-40" style={{
        touchAction: 'none'
      }} data-id="element-894" />
          <div onMouseDown={handleResizeStart('w')} className="absolute left-0 top-4 bottom-4 w-2 cursor-w-resize z-40" style={{
        touchAction: 'none'
      }} data-id="element-895" />
          <div onMouseDown={handleResizeStart('e')} className="absolute right-0 top-4 bottom-4 w-2 cursor-e-resize z-40" style={{
        touchAction: 'none'
      }} data-id="element-896" />
        </>}
      <div className={`h-12 ${theme === 'light' && !highContrast ? 'bg-white/95 backdrop-blur-xl' : themeClasses.card} border-b ${themeClasses.border} flex items-center justify-between px-4 cursor-move select-none`} data-id="element-897">
        <div className="flex items-center space-x-3" data-id="element-898">
          <div className="flex items-center space-x-2" role="group" aria-label="Window controls" data-id="element-899">
            <motion.button whileHover={{
            scale: 1.15
          }} whileTap={{
            scale: 0.9
          }} onClick={handleClose} className="w-3 h-3 rounded-full bg-gradient-to-br from-red-400 to-red-600 hover:from-red-500 hover:to-red-700 transition-all flex items-center justify-center group relative shadow-sm" aria-label={`Close ${window.title}`} data-id="element-900">
              <XIcon className="w-1.5 h-1.5 text-red-900 opacity-0 group-hover:opacity-100 transition-opacity" strokeWidth={3} data-id="element-901" />
            </motion.button>
            <motion.button whileHover={{
            scale: 1.15
          }} whileTap={{
            scale: 0.9
          }} onClick={handleMinimize} className="w-3 h-3 rounded-full bg-gradient-to-br from-amber-400 to-amber-600 hover:from-amber-500 hover:to-amber-700 transition-all flex items-center justify-center group relative shadow-sm" aria-label={`Minimize ${window.title}`} data-id="element-902">
              <MinusIcon className="w-1.5 h-1.5 text-amber-900 opacity-0 group-hover:opacity-100 transition-opacity" strokeWidth={3} data-id="element-903" />
            </motion.button>
            <motion.button whileHover={{
            scale: 1.15
          }} whileTap={{
            scale: 0.9
          }} onClick={handleMaximize} className="w-3 h-3 rounded-full bg-gradient-to-br from-emerald-400 to-emerald-600 hover:from-emerald-500 hover:to-emerald-700 transition-all flex items-center justify-center group relative shadow-sm" aria-label={`${window.isMaximized ? 'Restore' : 'Maximize'} ${window.title}`} data-id="element-904">
              <Minimize2Icon className="w-1.5 h-1.5 text-emerald-900 opacity-0 group-hover:opacity-100 transition-opacity" strokeWidth={3} data-id="element-905" />
            </motion.button>
          </div>
          <span id={`window-title-${window.id}`} className={`${themeClasses.text} text-sm font-semibold tracking-tight`} data-id="element-906">
            {window.title}
          </span>
        </div>
      </div>
      <div className="h-[calc(100%-3rem)] overflow-auto custom-scrollbar" data-id="element-907">
        {children}
      </div>
    </motion.div>;
}